/**
 * プロジェクトAPI
 * プロジェクト、日次活動、割り当ての管理（統合版）
 */

import apiClient from '@/shared/api/client'
import type {
  DailyActivity,
  DailyActivityInsert,
  DailyActivityUpdate,
  DailyActivityWithAssignments,
  Project,
  ProjectListResponse,
  ProjectWithZones,
  GetProjectsParams
} from '@/features/projects/types'

/**
 * 工区データを含むプロジェクト一覧のレスポンス型
 */
export interface ProjectWithZonesListResponse {
  items: ProjectWithZones[]
  total: number
}

/**
 * プロジェクト一覧を取得
 * @param params - クエリパラメータ
 * @returns プロジェクト一覧と合計数
 */
export const getProjects = async (
  params: GetProjectsParams & { include_zones?: boolean } = {}
): Promise<ProjectListResponse | ProjectWithZonesListResponse> => {
  try {
    const response = await apiClient.get<ProjectListResponse | ProjectWithZonesListResponse>('projects', {
      params: params,
    })
    return response.data
  } catch (error) {
    console.error('Failed to fetch projects:', error)
    throw error
  }
};

/**
 * 工区データを含むプロジェクト一覧を取得
 * @param params - クエリパラメータ
 * @returns 工区データを含むプロジェクト一覧と合計数
 */
export const getProjectsWithZones = async (
  params: GetProjectsParams = {}
): Promise<ProjectWithZonesListResponse> => {
  try {
    const response = await apiClient.get<ProjectWithZonesListResponse>('projects', {
      params: { ...params, include_zones: true },
    })
    return response.data
  } catch (error) {
    console.error('Failed to fetch projects with zones:', error)
    throw error
  }
};

/**
 * 日次活動一覧を取得
 * @param projectId - プロジェクトID
 * @param params - クエリパラメータ
 * @returns 日次活動一覧
 */
export const getDailyActivities = async (projectId: number, params?: { year?: number; month?: number }): Promise<DailyActivity[]> => {
  try {
    const response = await apiClient.get(`projects/${projectId}/daily-activities`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch daily activities:', error);
    throw error;
  }
};

/**
 * 日次活動一覧のAPIレスポンス構造
 */
export interface DailyActivityListResponse {
  items: DailyActivityWithAssignments[];
  total: number;
}

/**
 * 日次活動一覧のクエリパラメータ
 */
export interface GetDailyActivitiesParams {
  start_date: string; // YYYY-MM-DD, required
  end_date: string;   // YYYY-MM-DD, required
  include_assignments?: boolean;
  skip?: number;
  limit?: number;
}

/**
 * 日次活動一覧を取得
 * @param projectId - プロジェクトID
 * @param params - クエリパラメータ
 * @returns 日次活動一覧
 */
export const getDailyActivitiesWithAssignments = async (
  projectId: string,
  params: GetDailyActivitiesParams
): Promise<DailyActivityListResponse> => {
  if (!params.start_date || !params.end_date) {
    throw new Error('Start date and end date are required to fetch daily activities.');
  }

  try {
    const response = await apiClient.get<DailyActivityListResponse>(
      `projects/${projectId}/daily-activities`,
      {
        params: {
          ...params,
          include_assignments: params.include_assignments !== false,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch daily activities for project ${projectId}:`, error);
    throw error;
  }
};

/**
 * 日次活動のメモを更新する API リクエスト
 * @param dailyActivityId 更新対象の日次活動 ID
 * @param notes 新しいメモの内容
 * @returns 更新された日次活動データ
 */
export const updateDailyActivityNote = async (dailyActivityId: number, notes: string): Promise<DailyActivity> => {
  const response = await apiClient.patch<DailyActivity>(`daily-activities/${dailyActivityId}`, { notes });
  return response.data;
};

/**
 * プロジェクト情報を更新する API リクエスト
 * @param projectId 更新対象のプロジェクト ID
 * @param data 更新するプロジェクト情報 (Partial<Project> or ProjectUpdatePayload)
 * @returns 更新されたプロジェクトデータ
 */
export const updateProject = async (projectId: number, data: Partial<Project>): Promise<Project> => {
  const response = await apiClient.patch<Project>(`projects/${projectId}`, data);
  return response.data;
};

// =============================================================================
// 日次活動管理API
// =============================================================================

/**
 * 日次活動を作成
 */
export const createDailyActivity = async (data: DailyActivityInsert): Promise<DailyActivity> => {
  const response = await apiClient.post('/daily-activities', data)
  return response.data
}

/**
 * 日次活動を更新
 */
export const updateDailyActivity = async (id: string, data: DailyActivityUpdate): Promise<DailyActivity> => {
  const response = await apiClient.patch(`/daily-activities/${id}`, data)
  return response.data
}

/**
 * 日次活動を削除
 */
export const deleteDailyActivity = async (id: string): Promise<void> => {
  await apiClient.delete(`/daily-activities/${id}`)
}

/**
 * 指定された日次活動IDの詳細（割り当て情報を含む）を取得
 */
export const getDailyActivityWithAssignments = async (
  activityId: string
): Promise<DailyActivityWithAssignments> => {
  try {
    if (!activityId) {
      throw new Error("Activity ID is required.");
    }
    const response = await apiClient.get<DailyActivityWithAssignments>(
      `daily-activities/${activityId}`
    )
    return response.data
  } catch (error) {
    console.error(`Failed to fetch daily activity ${activityId} with assignments:`, error);
    throw error;
  }
};

/**
 * プロジェクトの日次活動を取得（年月または期間指定）
 */
export const getDailyActivitiesForProject = async (
  projectId: string,
  { year, month, startDate, endDate }: {
    year?: number;
    month?: number;
    startDate?: string;
    endDate?: string;
  }
): Promise<{ data: DailyActivityWithAssignments[]; error?: { message: string } }> => {
  try {
    let formattedStartDate: string;
    let formattedEndDate: string;

    if (startDate && endDate) {
      formattedStartDate = startDate;
      formattedEndDate = endDate;
    } else if (year !== undefined && month !== undefined) {
      const startDateObj = new Date(year, month - 1, 1);
      const endDateObj = new Date(year, month, 0);
      formattedStartDate = startDateObj.toISOString().split('T')[0];
      formattedEndDate = endDateObj.toISOString().split('T')[0];
    } else {
      throw new Error('年月または日付範囲の指定が必要です');
    }

    const data = await fetchDailyActivitiesWithAssignmentsByProject(
      projectId,
      formattedStartDate,
      formattedEndDate,
      true
    );

    return { data };
  } catch (error) {
    console.error(`Failed to get daily activities for project ${projectId}:`, error);
    return {
      data: [],
      error: { message: error instanceof Error ? error.message : '日次活動データの取得に失敗しました' }
    };
  }
};

/**
 * プロジェクトの特定期間の日次活動情報と割り当て情報を取得
 */
export const fetchDailyActivitiesWithAssignmentsByProject = async (
  projectId: string,
  startDate: string,
  endDate: string,
  includeAssignments: boolean = true
): Promise<DailyActivityWithAssignments[]> => {
  try {
    if (!projectId) {
      throw new Error("Project ID is required.");
    }
    if (!startDate || !endDate) {
      throw new Error("Start date and end date are required.");
    }

    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_assignments: String(includeAssignments),
    });

    const response = await apiClient.get<{ items: DailyActivityWithAssignments[]; total: number }>(
      `projects/${projectId}/daily-activities?${params.toString()}`
    );

    const normalizedItems = (response.data.items || []).map((activity): DailyActivityWithAssignments => {
      const normalizedActivity: Partial<DailyActivityWithAssignments> = { ...activity };

      if ((activity as unknown as { activity_date?: string }).activity_date && !normalizedActivity.schedule_date) {
        normalizedActivity.schedule_date = (activity as unknown as { activity_date?: string }).activity_date!;
      }

      if (Array.isArray(activity.craftsman_assignments)) {
        normalizedActivity.craftsman_assignments = activity.craftsman_assignments.map(a => ({ ...a }));
      }

      if (Array.isArray(activity.truck_assignments)) {
        normalizedActivity.truck_assignments = activity.truck_assignments.map(a => ({ ...a }));
      }

      return normalizedActivity as DailyActivityWithAssignments;
    });

    return normalizedItems;
  } catch (error) {
    console.error(`Failed to fetch daily activities for project ${projectId}:`, error);
    throw error;
  }
};

/**
 * 複数プロジェクトの日次活動を一括取得
 */
export const getBatchedDailyActivitiesForUserSchedule = async (
  projectIds: string[],
  { year, month, startDate, endDate }: {
    year?: number;
    month?: number;
    startDate?: string;
    endDate?: string;
  }
): Promise<{ data: Map<string, DailyActivityWithAssignments[]>; errors: string[] }> => {
  const activitiesMap = new Map<string, DailyActivityWithAssignments[]>();
  const errors: string[] = [];
  const requestCache = new Map<string, Promise<{ data: DailyActivityWithAssignments[]; error?: { message: string } }>>();

  try {
    let formattedStartDate: string;
    let formattedEndDate: string;

    if (startDate && endDate) {
      formattedStartDate = startDate;
      formattedEndDate = endDate;
    } else if (year !== undefined && month !== undefined) {
      const startDateObj = new Date(year, month - 1, 1);
      const endDateObj = new Date(year, month, 0);
      formattedStartDate = startDateObj.toISOString().split('T')[0];
      formattedEndDate = endDateObj.toISOString().split('T')[0];
    } else {
      throw new Error('年月または日付範囲の指定が必要です');
    }

    const requests = projectIds.map(projectId => {
      const cacheKey = `${projectId}-${formattedStartDate}-${formattedEndDate}`;

      if (!requestCache.has(cacheKey)) {
        const request = getDailyActivitiesForProject(projectId, { startDate: formattedStartDate, endDate: formattedEndDate });
        requestCache.set(cacheKey, request);
      }

      return {
        projectId,
        request: requestCache.get(cacheKey)!
      };
    });

    const results = await Promise.allSettled(
      requests.map(({ projectId, request }) =>
        request.then(result => ({ projectId, result }))
      )
    );

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const { projectId, result: apiResult } = result.value;
        if (apiResult.error) {
          errors.push(`Project ${projectId}: ${apiResult.error.message}`);
        } else {
          activitiesMap.set(projectId, apiResult.data);
        }
      } else {
        const projectId = requests[index].projectId;
        errors.push(`Project ${projectId}: ${result.reason}`);
        console.error(`Failed to fetch activities for project ${projectId}:`, result.reason);
      }
    });

    return { data: activitiesMap, errors };
  } catch (error) {
    console.error('Failed to fetch batched daily activities:', error);
    return {
      data: activitiesMap,
      errors: [error instanceof Error ? error.message : 'バッチリクエストに失敗しました']
    };
  }
};

// =============================================================================
// 職人割り当て管理API
// =============================================================================

/**
 * 職人割り当ての取得
 */
export const getCraftsmanAssignments = async (craftsmanId?: string, projectId?: string): Promise<DailyActivity[]> => {
  const params: Record<string, string> = {}
  if (craftsmanId) params.craftsman_id = craftsmanId
  if (projectId) params.project_id = projectId

  const response = await apiClient.get('/assignments/craftsman', { params })
  return response.data
}

/**
 * 職人割り当てを作成
 */
export const createCraftsmanAssignment = async ({
  projectId,
  date,
  zoneId,
  craftsmanId,
  vehicleId,
  notes: _notes
}: {
  projectId: string;
  date: string;
  zoneId: string | null;
  craftsmanId: string;
  vehicleId?: string | null;
  notes?: string;
}): Promise<{ data: import('@/features/projects/types').CraftsmanAssignment; error?: { message: string } }> => {
  try {
    const activityResponse = await fetchOrCreateDailyActivity({
      projectId,
      date,
      zoneId
    });

    if (activityResponse.error) {
      throw new Error(activityResponse.error.message);
    }

    const activityId = activityResponse.data?.id;
    if (!activityId) {
      throw new Error('日次活動IDの取得に失敗しました');
    }

    const requestData = {
      activity_id: activityId,
      craftsman_id: craftsmanId,
      vehicle_id: vehicleId || null,
      daily_rate: null
    };
    const response = await apiClient.post('craftsman-assignments/', requestData);
    return { data: response.data };
  } catch (error) {
    console.error('Failed to create craftsman assignment:', error);
    return {
      data: null as any,
      error: { message: error instanceof Error ? error.message : '職人の割り当てに失敗しました' }
    };
  }
};

/**
 * 職人割り当てを更新
 */
export const updateCraftsmanAssignment = async (
  id: string,
  data: { note?: string }
): Promise<{ data: import('@/features/projects/types').CraftsmanAssignment | null; error?: { message: string } }> => {
  try {
    const response = await apiClient.patch(`craftsman-assignments/${id}`, data);
    return { data: response.data };
  } catch (error) {
    console.error(`Failed to update craftsman assignment ${id}:`, error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : '職人の割り当て更新に失敗しました' }
    };
  }
};

/**
 * 職人割り当てを削除
 */
export const deleteCraftsmanAssignment = async (
  id: string
): Promise<{ success: boolean; error?: { message: string } }> => {
  try {
    await apiClient.delete(`craftsman-assignments/${id}`);
    return { success: true };
  } catch (error) {
    console.error(`Failed to delete craftsman assignment ${id}:`, error);
    return {
      success: false,
      error: { message: error instanceof Error ? error.message : '職人の割り当て削除に失敗しました' }
    };
  }
};

// =============================================================================
// 車両割り当て管理API
// =============================================================================

/**
 * 車両割り当ての取得
 */
export const getTruckAssignments = async (truckId?: string, projectId?: string): Promise<DailyActivity[]> => {
  const params: Record<string, string> = {}
  if (truckId) params.truck_id = truckId
  if (projectId) params.project_id = projectId

  const response = await apiClient.get('/assignments/truck', { params })
  return response.data
}

/**
 * 車両割り当てを作成
 */
export const createTruckAssignment = async ({
  projectId,
  date,
  zoneId,
  truckId,
  driverId,
  notes,
}: {
  projectId: string;
  date: string;
  zoneId: string | null;
  truckId: string;
  driverId?: string | null;
  notes?: string | null;
}): Promise<{ data: any; error?: { message: string } }> => {
  try {
    const activityResponse = await fetchOrCreateDailyActivity({
      projectId,
      date,
      zoneId
    });

    if (activityResponse.error) {
      throw new Error(activityResponse.error.message);
    }

    const activityId = activityResponse.data?.id;
    if (!activityId) {
      throw new Error('日次活動IDの取得に失敗しました');
    }

    const requestData = {
      activity_id: activityId,
      truck_id: truckId,
      driver_id: driverId || null,
      truck_cost_at_assignment: null,
      driver_rate_at_assignment: null
    };

    const response = await apiClient.post('truck-assignments/', requestData);
    return { data: response.data };
  } catch (error) {
    console.error('Failed to create truck assignment:', error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : 'トラックの割り当てに失敗しました' }
    };
  }
};

/**
 * 車両割り当てを更新
 */
export const updateTruckAssignment = async (
  id: string,
  data: { truck_driver_id?: string; note?: string }
): Promise<{ data: any; error?: { message: string } }> => {
  try {
    const response = await apiClient.patch(`truck-assignments/${id}`, data);
    return { data: response.data };
  } catch (error) {
    console.error(`Failed to update truck assignment ${id}:`, error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : 'トラックの割り当て更新に失敗しました' }
    };
  }
};

/**
 * 車両割り当てを削除
 */
export const deleteTruckAssignment = async (
  id: string
): Promise<{ success: boolean; error?: { message: string } }> => {
  try {
    await apiClient.delete(`truck-assignments/${id}`);
    return { success: true };
  } catch (error) {
    console.error(`Failed to delete truck assignment ${id}:`, error);
    return {
      success: false,
      error: { message: error instanceof Error ? error.message : 'トラックの割り当て削除に失敗しました' }
    };
  }
};

/**
 * 汎用割り当て更新
 */
export const updateAssignment = async (id: string, data: DailyActivityUpdate): Promise<DailyActivity> => {
  const response = await apiClient.patch(`/assignments/${id}`, data)
  return response.data
}

/**
 * 汎用割り当て削除
 */
export const deleteAssignment = async (id: string): Promise<void> => {
  await apiClient.delete(`/assignments/${id}`)
}

// =============================================================================
// 日次活動メモ管理API
// =============================================================================

/**
 * 日次活動のメモを更新（POSTベース）
 */
export const updateDailyActivityNotes = async (
  activityId: string,
  notes: string,
  activityInfo?: any
): Promise<{ data: any; error?: { message: string } }> => {
  try {
    if (!activityInfo) {
      return {
        data: null,
        error: { message: '日次活動の更新に必要な情報が不足しています' }
      };
    }

    const scheduleDateToUse = activityInfo.schedule_date || activityInfo.activity_date;

    if (!activityInfo.project_id || !scheduleDateToUse) {
      throw new Error('プロジェクトIDまたは日付の情報が不足しています');
    }

    const response = await apiClient.post('daily-activities', {
      project_id: activityInfo.project_id,
      schedule_date: scheduleDateToUse,
      zone_id: activityInfo.zone_id,
      notes: notes
    });

    return { data: response.data };
  } catch (error) {
    console.error(`Failed to update daily activity notes ${activityId}:`, error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : '日次メモの更新に失敗しました' }
    };
  }
};

/**
 * 日次活動のメモをUpsert
 */
export const upsertDailyActivityNotes = async ({
  projectId,
  date,
  zoneId,
  notes
}: {
  projectId: string;
  date: string;
  zoneId: string | null;
  notes: string;
}): Promise<{ data: any; error?: { message: string } }> => {
  try {
    const activityResponse = await fetchOrCreateDailyActivity({
      projectId,
      date,
      zoneId
    });

    if (activityResponse.error) {
      throw new Error(activityResponse.error.message);
    }

    const targetActivity = activityResponse.data;

    if (!targetActivity) {
      throw new Error('日次活動の取得または作成に失敗しました');
    }

    return await updateDailyActivityNotes(targetActivity.id, notes, targetActivity);
  } catch (error) {
    console.error('Failed to upsert daily activity notes:', error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : '日次メモの保存に失敗しました' }
    };
  }
};

// =============================================================================
// ヘルパー関数
// =============================================================================

/**
 * 日次活動IDを取得または作成
 */
async function fetchOrCreateDailyActivity({
  projectId,
  date,
  zoneId
}: {
  projectId: string;
  date: string;
  zoneId: string | null;
}): Promise<{ data: any; error?: { message: string } }> {
  try {
    const activitiesResponse = await getDailyActivitiesForProject(projectId, {
      startDate: date,
      endDate: date
    });

    if (activitiesResponse.error) {
      throw new Error(activitiesResponse.error.message);
    }

    const targetActivity = activitiesResponse.data?.find(activity => {
      const activityDate = activity.schedule_date || null;
      const activityAny = activity as any;
      const scheduleDate = activityAny.schedule_date || null;

      const dateMatch =
        (activityDate && typeof activityDate === 'string' && activityDate.startsWith(date)) ||
        (scheduleDate && typeof scheduleDate === 'string' && scheduleDate.startsWith(date));

      const zoneMatch = activity.zone_id === zoneId || (zoneId === null && activity.zone_id === null);

      return dateMatch && zoneMatch;
    });

    if (targetActivity) {
      return { data: targetActivity };
    } else {
      const createResponse = await apiClient.post('daily-activities', {
        project_id: projectId,
        zone_id: zoneId,
        schedule_date: date,
        notes: ''
      });

      return { data: createResponse.data };
    }
  } catch (error) {
    console.error('Failed to fetch or create daily activity:', error);
    return {
      data: null,
      error: { message: error instanceof Error ? error.message : '日次活動の取得・作成に失敗しました' }
    };
  }
}